import type { ReactNode } from "react";

type TickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  colorByValue?: Record<string, string>;
  // 눈금에 표시할 글자다. recharts 는 카테고리 값으로 시리즈를 구분하므로 값은 유일해야 한다.
  // 사람이 읽는 이름은 겹칠 수 있다. 그래서 값과 표기를 따로 받는다.
  labelByValue?: Record<string, string>;
  offset?: number;
  // recharts 는 축 폭과 눈금 개수를 눈금 컴포넌트에 함께 전달한다.
  width?: number;
  visibleTicksCount?: number;
};

const FONT_SIZE = 10;
// 옆 눈금과 붙어 보이지 않게 남기는 여백이다.
const TICK_GAP = 8;
const MIN_CHARS = 4;
// recharts 는 눈금마다 축 폭과 눈금 개수를 전달한다. 이 축에서는 폭을 모르는 경우가 생기지 않는다.
// 그래서 글자 수 상한 같은 두 번째 규칙을 두지 않는다. 규칙이 둘이면 잘린 이유를 추적하기 어렵다.

// SVG <text> 에는 text-overflow 가 적용되지 않는다. 그래서 글자 폭을 직접 추정한다.
// 한글·한자·가나는 글자 하나가 글꼴 크기만큼 넓고, 라틴 문자는 그 절반쯤이다.
// 눈금은 글자마다 이 검사를 하고, 차트는 hover 마다 눈금을 다시 그린다. 그래서 정규식은 모듈에서
// 한 번만 만든다.
const WIDE_CHAR = /[\u1100-\u11ff\u2e80-\u9fff\uac00-\ud7ff\uff00-\uff60]/u;

function charWidth(char: string): number {
  return WIDE_CHAR.test(char) ? FONT_SIZE : FONT_SIZE * 0.55;
}

function textWidth(text: string): number {
  let total = 0;
  for (const char of text) total += charWidth(char);
  return total;
}

/** 눈금 한 칸의 폭에 맞춰 글자를 줄인다. 폭을 모르면 그대로 둔다. */
export function fitAxisLabel(text: string, available: number | null): string {
  if (available === null || textWidth(text) <= available) return text;
  const budget = available - charWidth("…");
  let width = 0;
  let cut = 0;
  for (const char of text) {
    const next = width + charWidth(char);
    if (next > budget) break;
    width = next;
    cut += char.length;
  }
  return `${text.slice(0, Math.max(MIN_CHARS, cut))}…`;
}

// Recharts x축 눈금이다. run 색으로 칠하고, 눈금마다 실제로 받는 폭에 맞춰 자른다.
export function CompareAxisTick({
  x = 0,
  y = 0,
  payload,
  colorByValue = {},
  labelByValue = {},
  offset = 10,
  width,
  visibleTicksCount,
}: TickProps): ReactNode {
  const value = String(payload?.value ?? "");
  const text = labelByValue[value] ?? value;
  const slot =
    width !== undefined && visibleTicksCount !== undefined && visibleTicksCount > 0
      ? width / visibleTicksCount - TICK_GAP
      : null;
  const label = fitAxisLabel(text, slot !== null && slot > 0 ? slot : null);
  return (
    <g transform={`translate(${x},${y + offset})`}>
      <text
        textAnchor="middle"
        fill={colorByValue[value] ?? "var(--c6-color-text-muted)"}
        fontSize={FONT_SIZE}
      >
        {label}
      </text>
    </g>
  );
}
